import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ArrowLeft, Building2, Coins, Loader2 } from 'lucide-react'
import {
  formatCredits, getCatalog, getTenantWallet,
  type CatalogPack, type LedgerEntry,
} from '@/api/billing'
import { useRazorpayCheckout } from '@/hooks/useRazorpayCheckout'
import { refreshCredits } from '@/store/creditsStore'
import TopUpPacks from '@/pages/billing/TopUpPacks'
import TransactionsTable from '@/pages/billing/TransactionsTable'

interface MemberUsage {
  user_id: string
  user_name: string
  credits: number
  calls: number
}

/**
 * The organization's shared credit wallet.
 *
 * This is the wallet that pays whenever a member's LLM scope is `tenant` — so
 * it is also where an admin sees WHY it is draining, member by member.
 */
export default function TenantBillingPage() {
  const navigate = useNavigate()
  const { checkout, busy } = useRazorpayCheckout()

  const [balance, setBalance] = useState<number | null>(null)
  const [entries, setEntries] = useState<LedgerEntry[]>([])
  const [usage, setUsage] = useState<MemberUsage[]>([])
  const [packs, setPacks] = useState<CatalogPack[]>([])
  const [loading, setLoading] = useState(true)
  const [denied, setDenied] = useState(false)

  const load = useCallback(async () => {
    try {
      const [wallet, catalog] = await Promise.all([
        getTenantWallet({ limit: 50 }),
        getCatalog().catch(() => null),
      ])
      setBalance(wallet.wallet.balance)
      setEntries(wallet.entries)
      setUsage(wallet.member_usage)
      setPacks(catalog?.packs ?? [])
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 403 || status === 400) setDenied(true)
      else toast.error('Could not load the organization wallet')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const buy = useCallback(async (packCode: string, couponCode?: string) => {
    const result = await checkout({
      purpose: 'tenant_wallet_topup',
      target_ref: packCode,
      coupon_code: couponCode,
      description: 'Organization credits',
    })
    if (result.cancelled) return
    if (!result.ok) {
      toast.error(result.error || 'The payment did not complete')
      return
    }
    toast.success('Credits added to the organization wallet')
    refreshCredits()
    await load()
  }, [checkout, load])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <Loader2 className="spin" size={28} />
      </div>
    )
  }

  if (denied) {
    return (
      <div style={{ maxWidth: 640, margin: '3rem auto', padding: '0 1rem' }}>
        <div className="card" style={{ padding: '1.5rem' }}>
          <h2 style={{ marginTop: 0, fontSize: '1.1875rem' }}>Organization billing</h2>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem' }}>
            Only an organization admin can view or top up the shared wallet.
            Ask your admin, or manage your own credits from Billing &amp; Plans.
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/billing')}>
            My billing
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '1.5rem 1rem 3rem' }}>
      <button
        className="btn btn-secondary"
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/builder'))}
        style={{ marginBottom: '1rem', gap: '0.375rem' }}
      >
        <ArrowLeft size={16} /> Back
      </button>

      <h1 style={{ fontSize: '1.5625rem', margin: '0 0 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Building2 size={22} /> Organization credits
      </h1>

      <div className="card" style={{ padding: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
          <Coins size={16} style={{ color: 'var(--color-accent)' }} /> Shared wallet balance
        </div>
        <div style={{ fontSize: '2.3125rem', fontWeight: 700, lineHeight: 1.2, marginTop: '0.25rem' }}>
          {formatCredits(balance)}
        </div>
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>
          AI usage by members whose LLM settings are organization-scoped is debited
          from this wallet. Members using their own settings or their own API key
          are billed to themselves.
        </p>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <TopUpPacks packs={packs} busy={busy} onBuy={buy} heading="Top up the organization wallet" />
      </div>

      <div className="card" style={{ padding: '1.25rem', marginTop: '1rem' }}>
        <h3 style={{ margin: '0 0 0.875rem', fontSize: '1.0625rem' }}>Usage by member this month</h3>
        {!usage.length ? (
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', margin: 0 }}>
            No organization-billed AI usage yet this month.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9375rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>
                <th style={{ padding: '0.5rem 0.5rem 0.5rem 0' }}>Member</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Calls</th>
                <th style={{ padding: '0.5rem 0 0.5rem 0.5rem', textAlign: 'right' }}>Credits</th>
              </tr>
            </thead>
            <tbody>
              {usage.map((u) => (
                <tr key={u.user_id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.5rem 0.5rem 0.5rem 0' }}>{u.user_name}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {u.calls.toLocaleString()}
                  </td>
                  <td style={{ padding: '0.5rem 0 0.5rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatCredits(u.credits)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: '1rem' }}>
        <TransactionsTable
          entries={entries}
          title="Organization credit history"
          emptyText="No credit activity on the organization wallet yet."
        />
      </div>
    </div>
  )
}
